import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { jwtDecode } from "jwt-decode";

/**
 * Attempts to get a valid access token:
 * - Uses silent acquisition first.
 * - Falls back to interactive popup if silent fails due to consent/session expiration.
 *
 * @param {PublicClientApplication} msalInstance - MSAL instance
 * @return {Promise<string|null>} - Resolves to access token or null if no account or failure.
 */
export const getAccessToken = async (msalInstance) => {
  const accounts = msalInstance.getAllAccounts();

  if (!accounts.length) {
    console.warn("No signed-in accounts found.");
    return null;
  }

  const activeAccount = accounts[0];
  msalInstance.setActiveAccount(activeAccount);

  const tokenRequest = {
    scopes: [`${process.env.REACT_APP_CLIENT_ID}/.default`],
    account: activeAccount,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    if (response && response.accessToken) {
     return response.accessToken;
    }
  } catch (error) {
    console.warn("Silent token acquisition failed:", error);

    // If interaction required, fallback to popup
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(tokenRequest);
        if (response && response.accessToken) {
          console.info("Acquired token via popup.");
          return response.accessToken;
        }
      } catch (popupError) {
        console.error("Interactive token acquisition failed:", popupError);
        return null;
      }
    } else {
      // Some other error - consider logging or rethrowing
      console.error("Non-interaction error during token acquisition:", error);
      return null;
    }
  }

  return null;
};

/**
 * Checks if the token is expired.
 * @param {string} accessToken - JWT access token
 * @returns {boolean} true if expired, false otherwise
 */
export const isTokenExpired = (accessToken) => {
  if (!accessToken) return true;

  try {
    const decoded = jwtDecode(accessToken);
    // exp claim in seconds; convert to ms and compare with Date.now()
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    console.error("Failed to decode token:", error);
    return true;
  }
};

/**
 * Wrapper to get a valid token, refreshing it if expired.
 * @param {PublicClientApplication} msalInstance
 * @param {string|null} currentToken
 * @returns {Promise<string|null>} Valid token or null
 */
export const getValidAccessToken = async (msalInstance, currentToken) => {
  if (!currentToken || isTokenExpired(currentToken)) {
    return await getAccessToken(msalInstance);
  }
  return currentToken;
};